import { useMemo } from 'react';
import jsYaml from 'js-yaml';

import { convertMiBToGB, renderRange, round } from 'pages/Offers/List/helpers';

import { IRunEnvironmentFormValues } from '../types';

export type UseGenerateYamlArgs = {
    formValues: IRunEnvironmentFormValues;
    configuration?: ITemplate['configuration'];
    envParam?: TTemplateParam;
    hasResourcesParam?: boolean;
    backends?: string[];
    fleets?: string[];
};

export const useGenerateYaml = ({
    formValues,
    configuration,
    envParam,
    hasResourcesParam,
    backends,
    fleets,
}: UseGenerateYamlArgs) => {
    return useMemo(() => {
        const { name, ide, image, python, offer, repo_url, repo_path, working_dir, password, gpu_enabled } = formValues;
        const gpuEnabled = gpu_enabled === true;
        const hasTemplateResources =
            configuration &&
            'resources' in configuration &&
            configuration.resources &&
            typeof configuration.resources === 'object'
                ? true
                : false;
        const baseResources =
            hasTemplateResources && configuration ? (configuration.resources as Record<string, unknown>) : undefined;
        const hasTemplateGpu = !!baseResources && 'gpu' in baseResources;

        const envEntries: string[] = [];
        if (envParam?.name && password) {
            envEntries.push(`${envParam.name}=${password}`);
        }
        if (configuration && 'env' in configuration) {
            envEntries.push(...(configuration['env'] as string[]));
        }

        return jsYaml.dump(
            {
                ...configuration,

                ...(name ? { name } : {}),
                ...(ide ? { ide } : {}),
                ...(image ? { image } : {}),
                ...(python ? { python } : {}),
                ...(envEntries.length > 0 ? { env: envEntries } : {}),

                ...(gpuEnabled && offer
                    ? {
                          resources: {
                              ...baseResources,
                              gpu: `${offer.name}:${round(convertMiBToGB(offer.memory_mib))}GB:${renderRange(offer.count)}`,
                          },

                          ...(backends && backends.length > 0 ? { backends } : {}),
                          ...(fleets && fleets.length > 0 ? { fleets } : {}),
                          ...(offer.spot.length === 1 ? { spot_policy: offer.spot[0] } : {}),
                          ...(offer.spot.length > 1 ? { spot_policy: 'auto' } : {}),
                      }
                    : {}),
                ...(gpuEnabled && !offer && hasResourcesParam && !hasTemplateGpu
                    ? {
                          resources: {
                              ...baseResources,
                              gpu: '1..',
                          },
                      }
                    : {}),
                ...(hasResourcesParam && !gpuEnabled
                    ? {
                          resources: {
                              ...baseResources,
                              gpu: 0,
                          },
                      }
                    : {}),

                ...(repo_url || repo_path
                    ? {
                          repos: [[repo_url?.trim(), repo_path?.trim()].filter(Boolean).join(':')],
                      }
                    : {}),

                ...(working_dir ? { working_dir } : {}),
            },
            { lineWidth: -1 },
        );
    }, [formValues, configuration, envParam, hasResourcesParam, backends, fleets]);
};
